import React, { useEffect, useState, createContext, useContext } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, signOut, onAuthStateChanged, User, sendEmailVerification, updateProfile, updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential, applyActionCode } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db, actionCodeSettings } from '../firebase';
import { detectUserTimezone } from '../utils/timezoneUtils';
interface AuthContextType {
  currentUser: User | null;
  userData: any;
  userType: string | null;
  loading: boolean;
  signUp: (email: string, password: string, userType: string) => Promise<any>;
  registerTeacher: (email: string, password: string, firstName: string, lastName: string, location: string, city: string, state: string) => Promise<any>;
  registerStudent: (email: string, password: string, firstName: string, lastName: string) => Promise<any>;
  login: (email: string, password: string, userType?: string) => Promise<any>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUserProfile: (data: any) => Promise<void>;
  updateUserEmail: (email: string, password: string) => Promise<void>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  verifyEmail: (actionCode: string) => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Sign up function
  async function signUp(email: string, password: string, type: string) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // Create user document in Firestore
      const userRef = doc(db, type === 'teacher' ? 'teachers' : 'students', user.uid);
      await setDoc(userRef, {
        email: email.toLowerCase(),
        userType: type,
        createdAt: new Date()
      });
      // Send verification email using Firebase's built-in method
      await sendEmailVerification(user, actionCodeSettings);
      // Store user type in local storage for persistence
      localStorage.setItem('userType', type);
      setUserType(type);
      return userCredential;
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  }
  // FIXED: Enhanced registerTeacher function with automatic timezone detection
  async function registerTeacher(email: string, password: string, firstName: string, lastName: string, location: string, city: string, state: string) {
    try {
      console.log('Starting teacher registration with email:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('User created successfully:', user.uid);
      const displayName = `${firstName} ${lastName}`.trim();
      // Update profile with display name
      await updateProfile(user, {
        displayName
      });
      console.log('Profile updated with displayName:', displayName);
      // FEATURE: Auto-detect timezone during signup
      const detectedTimezone = detectUserTimezone();
      console.log('[Signup] Detected timezone:', detectedTimezone);
      // Create teacher document in Firestore with auto-detected timezone
      const teacherRef = doc(db, 'teachers', user.uid);
      await setDoc(teacherRef, {
        uid: user.uid,
        email: email.toLowerCase(),
        firstName,
        lastName,
        displayName,
        location,
        city,
        state,
        userType: 'teacher',
        isVerified: false,
        // Auto-detected timezone (fallback to Eastern if detection fails)
        timeZone: detectedTimezone || 'America/New_York',
        timezoneAutoDetected: !!detectedTimezone,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('Teacher document created in Firestore with timezone:', detectedTimezone || 'America/New_York (fallback)');
      // Also create in users collection for backward compatibility
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        uid: user.uid,
        email: email.toLowerCase(),
        displayName,
        firstName,
        lastName,
        userType: 'teacher',
        phone: '',
        address: {
          street: '',
          city,
          state,
          zipCode: '',
          country: 'United States'
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('User document created in Firestore for backward compatibility');
      // Store user type in local storage for persistence
      localStorage.setItem('userType', 'teacher');
      setUserType('teacher');
      // Store in localStorage for backward compatibility
      localStorage.setItem('teacherName', displayName);
      localStorage.setItem('teacherFirstName', firstName);
      localStorage.setItem('teacherLastName', lastName);
      localStorage.setItem('teacherEmail', email);
      localStorage.setItem('teacherLocation', location);
      localStorage.setItem('teacherCity', city);
      localStorage.setItem('teacherState', state);
      // FIXED: Enhanced verification email sending with proper error handling
      try {
        console.log('Sending verification email with actionCodeSettings:', JSON.stringify(actionCodeSettings));
        // Send verification email with proper settings
        await sendEmailVerification(user, actionCodeSettings);
        console.log('Verification email sent successfully');
      } catch (emailError: any) {
        console.error('Error sending verification email:', emailError);
        console.error('Error code:', emailError.code);
        console.error('Error message:', emailError.message);
        // Continue with registration but log the error
        // We don't throw here to allow the user to request a new verification email later
      }
      return userCredential;
    } catch (error: any) {
      console.error('Error registering teacher:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }
  // PHASE 1: Updated registerStudent to use users/{uid} as single source of truth
  async function registerStudent(email: string, password: string, firstName: string, lastName: string) {
    try {
      console.log('Starting student registration with email:', email);
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('User created successfully:', user.uid);
      const displayName = `${firstName} ${lastName}`.trim();
      // Update profile with display name
      await updateProfile(user, {
        displayName
      });
      console.log('Profile updated with displayName:', displayName);
      // PHASE 1 FIX: Create user document in users/{uid} as single source of truth
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        uid: user.uid,
        email: email.toLowerCase(),
        firstName,
        lastName,
        displayName,
        userType: 'student',
        // Onboarding tracking
        onboardingStep: 'email-verification',
        onboardingComplete: false,
        // Profile fields (to be completed during onboarding)
        phone: '',
        instrument: '',
        skillLevel: '',
        lessonPreferences: {},
        timezone: '',
        // Metadata
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        emailVerified: false
      });
      console.log('User document created in users collection');
      // Send verification email
      try {
        console.log('Sending verification email');
        await sendEmailVerification(user, actionCodeSettings);
        console.log('Verification email sent successfully');
      } catch (emailError) {
        console.error('Error sending verification email:', emailError);
        // Continue with registration process even if email fails
      }
      // PHASE 1 FIX: Store user type from custom claim (will be set by Cloud Function)
      // For now, store in localStorage as fallback until custom claims are deployed
      localStorage.setItem('userType', 'student');
      setUserType('student');
      // Store basic info in localStorage for backward compatibility
      localStorage.setItem('studentEmail', email);
      localStorage.setItem('studentName', displayName);
      localStorage.setItem('studentFirstName', firstName);
      localStorage.setItem('studentLastName', lastName);
      return userCredential;
    } catch (error: any) {
      console.error('Error registering student:', error);
      throw error;
    }
  }
  // PHASE 1: Updated login to check custom claims and users collection
  async function login(email: string, password: string, type?: string) {
    try {
      console.log('=== LOGIN DEBUG START ===');
      console.log('Email:', email);
      console.log('Type parameter received:', type);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('Firebase Auth successful, UID:', user.uid);
      // PHASE 1 FIX: Get custom claims from ID token
      const idTokenResult = await user.getIdTokenResult();
      const claimedUserType = idTokenResult.claims.userType as string | undefined;
      console.log('Custom claim userType:', claimedUserType);
      // If type is provided, verify it matches
      if (type) {
        console.log('Type validation enabled, expected type:', type);
        // Check custom claim first
        if (claimedUserType && claimedUserType !== type) {
          console.log('MISMATCH: Custom claim does not match expected type!');
          console.log('Expected:', type, 'Got:', claimedUserType);
          await signOut(auth);
          throw new Error(`Account type mismatch. Please use ${claimedUserType} login.`);
        }
        // Verify against Firestore as backup
        const userRef = doc(db, 'users', user.uid);
        console.log('Checking Firestore users collection for UID:', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const firestoreUserType = userDoc.data().userType;
          console.log('Firestore userType:', firestoreUserType);
          if (firestoreUserType !== type) {
            console.log('MISMATCH: Firestore userType does not match expected type!');
            console.log('Expected:', type, 'Got:', firestoreUserType);
            await signOut(auth);
            throw new Error(`No ${type} account found with this email`);
          }
          // Check for claim/Firestore mismatch
          if (claimedUserType && claimedUserType !== firestoreUserType) {
            console.error('Custom claim does not match Firestore userType!');
            await signOut(auth);
            throw new Error('Account data inconsistency detected. Please contact support.');
          }
          console.log('Validation passed! Setting userType:', firestoreUserType);
          localStorage.setItem('userType', firestoreUserType);
          setUserType(firestoreUserType);
        } else {
          console.log('User document not found in users collection');
          // Check legacy collections based on login type
          if (type === 'teacher') {
            console.log('Checking legacy teachers collection');
            const teacherRef = doc(db, 'teachers', user.uid);
            const teacherDoc = await getDoc(teacherRef);
            if (teacherDoc.exists()) {
              console.log('Found in teachers collection');
              localStorage.setItem('userType', 'teacher');
              setUserType('teacher');
            } else {
              console.log('Not found in teachers collection either, signing out');
              await signOut(auth);
              throw new Error(`No ${type} account found with this email`);
            }
          } else if (type === 'student') {
            // For student login, check legacy students collection
            console.log('Checking legacy students collection');
            const studentRef = doc(db, 'students', user.uid);
            const studentDoc = await getDoc(studentRef);
            if (studentDoc.exists()) {
              console.log('Found in students collection');
              const studentData = studentDoc.data();
              // Verify it's actually a student account
              if (studentData.userType === 'student' || !studentData.userType) {
                console.log('Confirmed as student account in legacy collection');
                localStorage.setItem('userType', 'student');
                setUserType('student');
              } else {
                console.log('Legacy document has wrong userType:', studentData.userType);
                await signOut(auth);
                throw new Error(`No ${type} account found with this email`);
              }
            } else {
              console.log('Not found in students collection either, signing out');
              await signOut(auth);
              throw new Error(`No ${type} account found with this email`);
            }
          } else {
            // Unknown type, fail
            console.log('Unknown account type, signing out');
            await signOut(auth);
            throw new Error(`No ${type} account found with this email`);
          }
        }
      } else {
        console.log('No type parameter provided, determining from claims/Firestore');
        // No type provided - determine from custom claim or Firestore
        let determinedType = claimedUserType;
        if (!determinedType) {
          // Fallback to Firestore
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            determinedType = userDoc.data().userType;
          } else {
            // Check legacy collections
            const teacherRef = doc(db, 'teachers', user.uid);
            const teacherDoc = await getDoc(teacherRef);
            if (teacherDoc.exists()) {
              determinedType = 'teacher';
            }
          }
        }
        if (determinedType) {
          localStorage.setItem('userType', determinedType);
          setUserType(determinedType);
        }
      }
      console.log('=== LOGIN DEBUG END - SUCCESS ===');
      return userCredential;
    } catch (error) {
      console.error('=== LOGIN DEBUG END - ERROR ===');
      console.error('Error logging in:', error);
      throw error;
    }
  }
  // Logout function
  async function logout() {
    try {
      await signOut(auth);
      localStorage.removeItem('userType');
      setUserType(null);
      setUserData(null);
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  }
  // Password reset function
  async function resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }
  // FIXED: Enhanced sendVerificationEmail function with better error handling
  async function sendVerificationEmail() {
    try {
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      console.log('Current user:', currentUser.uid);
      console.log('Email verified status before sending:', currentUser.emailVerified);
      console.log('Sending verification email with actionCodeSettings:', JSON.stringify(actionCodeSettings));
      // Send verification email with proper settings
      await sendEmailVerification(currentUser, actionCodeSettings);
      console.log('Verification email sent successfully');
      return true;
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      // Enhanced error handling with specific error types
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many verification emails sent. Please try again later.');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error. Please check your internet connection and try again.');
      } else {
        throw error;
      }
    }
  }
  // Verify email function with improved error handling
  async function verifyEmail(actionCode: string) {
    try {
      console.log('Applying action code for email verification:', actionCode);
      // Apply the action code to verify the email
      await applyActionCode(auth, actionCode);
      console.log('Action code applied successfully');
      // Force token refresh to update the emailVerified status
      if (currentUser) {
        console.log('Refreshing user token and data');
        try {
          await currentUser.getIdToken(true);
          console.log('Token refreshed successfully');
        } catch (tokenError) {
          console.error('Error refreshing token:', tokenError);
        }
        try {
          await currentUser.reload();
          console.log('User reloaded successfully');
          // Update the current user reference
          const updatedUser = auth.currentUser;
          if (updatedUser) {
            console.log('Email verified status after verification:', updatedUser.emailVerified);
            setCurrentUser(updatedUser);
          }
        } catch (reloadError) {
          console.error('Error reloading user after verification:', reloadError);
        }
      }
      return true;
    } catch (error: any) {
      console.error('Error verifying email:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }
  // FIXED: Enhanced refreshUser function to properly update emailVerified status
  async function refreshUser(): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        if (!currentUser) {
          reject(new Error('No user is currently signed in'));
          return;
        }
        console.log('Refreshing user data for:', currentUser.uid);
        console.log('Current email verified status:', currentUser.emailVerified);
        // First force token refresh
        try {
          await currentUser.getIdToken(true);
          console.log('Token refreshed successfully');
        } catch (tokenError) {
          console.error('Error refreshing token:', tokenError);
        }
        // Then reload user to get fresh data
        try {
          await currentUser.reload();
          console.log('User reloaded successfully');
        } catch (reloadError) {
          console.error('Error reloading user:', reloadError);
          reject(reloadError);
          return;
        }
        // Get the updated user reference
        const updatedUser = auth.currentUser;
        if (updatedUser) {
          console.log('Updated user retrieved successfully');
          console.log('Email verified status after refresh:', updatedUser.emailVerified);
          setCurrentUser(updatedUser);
          resolve();
        } else {
          console.error('User not found after reload');
          reject(new Error('User not found after reload'));
        }
      } catch (error) {
        console.error('Error in refreshUser:', error);
        reject(error);
      }
    });
  }
  // Update user profile function
  async function updateUserProfile(data: any) {
    try {
      if (!currentUser) throw new Error('No user logged in');
      // Update Firebase Auth profile if name or photo provided
      if (data.displayName || data.photoURL) {
        await updateProfile(currentUser, {
          displayName: data.displayName || currentUser.displayName,
          photoURL: data.photoURL || currentUser.photoURL
        });
      }
      // Update Firestore document
      if (userType) {
        const userRef = doc(db, userType === 'teacher' ? 'teachers' : 'students', currentUser.uid);
        await updateDoc(userRef, data);
        // Update local state
        setUserData((prevData: any) => ({
          ...prevData,
          ...data
        }));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }
  // Update user email function
  async function updateUserEmail(email: string, password: string) {
    try {
      if (!currentUser) throw new Error('No user logged in');
      // Re-authenticate the user
      const credential = EmailAuthProvider.credential(currentUser.email!, password);
      await reauthenticateWithCredential(currentUser, credential);
      // Update email in Firebase Auth
      await updateEmail(currentUser, email);
      // Update email in Firestore
      if (userType) {
        const userRef = doc(db, userType === 'teacher' ? 'teachers' : 'students', currentUser.uid);
        await updateDoc(userRef, {
          email: email.toLowerCase()
        });
        // Update local state
        setUserData((prevData: any) => ({
          ...prevData,
          email: email.toLowerCase()
        }));
      }
    } catch (error) {
      console.error('Error updating email:', error);
      throw error;
    }
  }
  // Update user password function
  async function updateUserPassword(currentPassword: string, newPassword: string) {
    try {
      if (!currentUser) throw new Error('No user logged in');
      // Re-authenticate the user
      const credential = EmailAuthProvider.credential(currentUser.email!, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      // Update password
      await updatePassword(currentUser, newPassword);
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  }
  // PHASE 1: Updated auth state listener to use custom claims
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);
      if (user) {
        try {
          // PHASE 1 FIX: Get userType from custom claims first
          const idTokenResult = await user.getIdTokenResult();
          const claimedUserType = idTokenResult.claims.userType as string | undefined;
          console.log('Auth state changed - custom claim userType:', claimedUserType);
          let finalUserType = claimedUserType;
          // Fallback to localStorage if no custom claim yet
          if (!finalUserType) {
            finalUserType = localStorage.getItem('userType');
            console.log('No custom claim, using localStorage:', finalUserType);
          }
          if (finalUserType) {
            setUserType(finalUserType);
            // Fetch user data from users collection
            const userRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const firestoreData = userDoc.data();
              // Validate userType matches
              if (firestoreData.userType !== finalUserType) {
                console.error('UserType mismatch between claim/localStorage and Firestore!');
                // Sign out for security
                await signOut(auth);
                setUserType(null);
                setUserData(null);
                return;
              }
              setUserData(firestoreData);
            } else if (finalUserType === 'teacher') {
              // Fallback for teachers in legacy collection
              const teacherRef = doc(db, 'teachers', user.uid);
              const teacherDoc = await getDoc(teacherRef);
              if (teacherDoc.exists()) {
                setUserData(teacherDoc.data());
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setUserType(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);
  const value = {
    currentUser,
    userData,
    userType,
    loading,
    signUp,
    registerTeacher,
    registerStudent,
    login,
    logout,
    resetPassword,
    sendVerificationEmail,
    refreshUser,
    updateUserProfile,
    updateUserEmail,
    updateUserPassword,
    verifyEmail
  };
  return <AuthContext.Provider value={value} data-id="element-9862">{children}</AuthContext.Provider>;
}